import { GOALS } from "./goalsConfig";
import type { GoalId } from "./types";

const DASHBOARD_LAYOUT_PREFERENCE_KEY = "goal-dashboard-layout-preference";
export const DEFAULT_HERO_GOAL_ID: GoalId = "lp_conversion";

export type BentoSize = "standard" | "wide" | "hero";

export interface GoalDashboardLayoutPreference {
  pinnedGoalId: GoalId | null;
}

export interface BentoLayoutItem {
  goalId: GoalId;
  size: BentoSize;
  colSpan: number;
  rowSpan: number;
}

const GOAL_IDS = GOALS.map((goal) => goal.id);

const EMPTY_PREFERENCE: GoalDashboardLayoutPreference = {
  pinnedGoalId: null,
};

const SIZE_AREA: Record<BentoSize, number> = {
  standard: 1,
  wide: 2,
  hero: 4,
};

function getLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isGoalId(value: unknown): value is GoalId {
  return typeof value === "string" && GOAL_IDS.includes(value as GoalId);
}

function normalizeGoalOrder(value: unknown): Array<GoalId> {
  const seen = new Set<GoalId>();
  const orderedIds = Array.isArray(value)
    ? value.filter((goalId): goalId is GoalId => {
        if (!isGoalId(goalId) || seen.has(goalId)) {
          return false;
        }
        seen.add(goalId);
        return true;
      })
    : [];

  GOAL_IDS.forEach((goalId) => {
    if (!seen.has(goalId)) {
      orderedIds.push(goalId);
    }
  });

  return orderedIds;
}

function normalizePreference(value: unknown): GoalDashboardLayoutPreference {
  if (!isRecord(value)) {
    return EMPTY_PREFERENCE;
  }

  return {
    pinnedGoalId: isGoalId(value.pinnedGoalId) ? value.pinnedGoalId : null,
  };
}

export function readGoalDashboardLayoutPreference(): GoalDashboardLayoutPreference {
  const storage = getLocalStorage();
  if (!storage) {
    return EMPTY_PREFERENCE;
  }

  try {
    const raw = storage.getItem(DASHBOARD_LAYOUT_PREFERENCE_KEY);
    if (raw) {
      const preference = normalizePreference(JSON.parse(raw));
      writeGoalDashboardLayoutPreference(preference);
      return preference;
    }

    writeGoalDashboardLayoutPreference(EMPTY_PREFERENCE);
    return EMPTY_PREFERENCE;
  } catch {
    return EMPTY_PREFERENCE;
  }
}

export function writeGoalDashboardLayoutPreference(
  preference: GoalDashboardLayoutPreference,
) {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      DASHBOARD_LAYOUT_PREFERENCE_KEY,
      JSON.stringify(normalizePreference(preference)),
    );
  } catch {
    // Layout preference is best-effort; the automatic bento layout is safe.
  }
}

export function setPinnedGoalId(pinnedGoalId: GoalId | null) {
  writeGoalDashboardLayoutPreference({ pinnedGoalId });
}

export function getHeroGoalId(
  preference: GoalDashboardLayoutPreference,
): GoalId {
  return preference.pinnedGoalId ?? DEFAULT_HERO_GOAL_ID;
}

export function getResolvedGoalOrder(
  preference: GoalDashboardLayoutPreference,
): Array<GoalId> {
  const heroGoalId = getHeroGoalId(preference);
  const order = [...GOAL_IDS];
  const heroIndex = order.indexOf(heroGoalId);
  const defaultHeroIndex = order.indexOf(DEFAULT_HERO_GOAL_ID);

  if (
    heroGoalId !== DEFAULT_HERO_GOAL_ID &&
    heroIndex !== -1 &&
    defaultHeroIndex !== -1
  ) {
    order[defaultHeroIndex] = heroGoalId;
    order[heroIndex] = DEFAULT_HERO_GOAL_ID;
  }

  return normalizeGoalOrder(order);
}

export function getResolvedGoalSizes(
  _preference: GoalDashboardLayoutPreference,
): Partial<Record<GoalId, BentoSize>> {
  return {};
}

function getTargetArea(currentArea: number, columns: number, autoCount: number) {
  if (autoCount === 0) {
    return currentArea;
  }

  const baseTargetArea = columns >= 4 ? 12 : columns === 2 ? 10 : currentArea;
  const minimumArea = Math.max(baseTargetArea, currentArea);
  return Math.ceil(minimumArea / columns) * columns;
}

function getSpans(size: BentoSize, columns: number) {
  if (size === "standard") {
    return { colSpan: 1, rowSpan: 1 };
  }

  if (size === "wide") {
    return { colSpan: Math.min(2, columns), rowSpan: 1 };
  }

  return {
    colSpan: Math.min(2, columns),
    rowSpan: columns === 1 ? 2 : 2,
  };
}

function promoteAutoCard(
  sizes: Record<GoalId, BentoSize>,
  autoGoalIds: Array<GoalId>,
  delta: 1 | 2 | 3,
) {
  const nextSizeByDelta: Record<1 | 2 | 3, Array<[BentoSize, BentoSize]>> = {
    1: [["standard", "wide"]],
    2: [["wide", "hero"]],
    3: [["standard", "hero"]],
  };

  for (const [from, to] of nextSizeByDelta[delta]) {
    const goalId = autoGoalIds.find((id) => sizes[id] === from);
    if (goalId) {
      sizes[goalId] = to;
      return true;
    }
  }

  return false;
}

export function computeBentoLayout(
  order: Array<GoalId>,
  sizes: Partial<Record<GoalId, BentoSize>>,
  columns: number,
): Array<BentoLayoutItem> {
  const normalizedColumns = Math.max(1, columns);
  const normalizedOrder = normalizeGoalOrder(order);
  const explicitGoalIds = new Set(Object.keys(sizes).filter(isGoalId));
  const resolvedSizes = normalizedOrder.reduce<Record<GoalId, BentoSize>>(
    (acc, goalId) => {
      acc[goalId] = sizes[goalId] ?? "standard";
      return acc;
    },
    {} as Record<GoalId, BentoSize>,
  );
  const autoGoalIds = normalizedOrder.filter((goalId) => !explicitGoalIds.has(goalId));
  const currentArea = normalizedOrder.reduce(
    (total, goalId) => total + SIZE_AREA[resolvedSizes[goalId]],
    0,
  );
  let remainingArea =
    getTargetArea(currentArea, normalizedColumns, autoGoalIds.length) - currentArea;

  while (remainingArea > 0) {
    if (remainingArea >= 3 && promoteAutoCard(resolvedSizes, autoGoalIds, 3)) {
      remainingArea -= 3;
      continue;
    }

    if (remainingArea >= 2 && promoteAutoCard(resolvedSizes, autoGoalIds, 2)) {
      remainingArea -= 2;
      continue;
    }

    if (promoteAutoCard(resolvedSizes, autoGoalIds, 1)) {
      remainingArea -= 1;
      continue;
    }

    break;
  }

  return normalizedOrder.map((goalId) => ({
    goalId,
    size: resolvedSizes[goalId],
    ...getSpans(resolvedSizes[goalId], normalizedColumns),
  }));
}
